import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Typography,
} from '@mui/material';

import { useModuleSettingsHook } from '../../../Hooks/Maintenance/ModulesSettings/modulesSettingsHooks';

import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';

import { TableContainer } from '../../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';

import QueryBuilderHelper from '../../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../../Utilities/Helpers/DateHelper';
import RouteHelper from '../../../Utilities/Helpers/RouteHelper';
import AccessibilityHelper from '../../../Utilities/Helpers/AccessibilityHelper';

import { CachingEnum } from '../../../Utilities/Enums/CachingEnum';
import { RouteEnum } from '../../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../../Utilities/Enums/ButtonTypeEnum';
import { ModuleSettingField } from '../../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../../Utilities/Enums/AdminModulesEnum';
import { OrderByEnum } from '../../../Utilities/Enums/SortEnums';

import { ModuleSetting } from '../../../Models/Maintenance/ModuleSetting/Module';

import CustomListComponent from '../../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomButton from '../../../CustomViews/Elements/CustomButton';
import CustomListHeader from '../../../CustomViews/Pages/ListComponent/CustomListHeader';
import CustomListSearch from '../../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import { CustomListSearchResetBtns } from '../../../CustomViews/Pages/ListComponent/Search/CustomListSearchResetBtns';
import FormDatePicker from '../../../CustomViews/Elements/FormDatePicker';
import FormTextField from '../../../CustomViews/Elements/FormTextField';
import AddEditModuleModal from './AddEditModuleModal';

export default function ModuleSettingList() {
    const navigate = useNavigate();
    const location = useLocation()

    //search params
    const queryParams = new URLSearchParams(location.search);
    const [searchParams, setSearchParams] = useSearchParams(queryParams);

    //use states
    const [openDelete, setopenDelete] = useState(false);
    const [openAddEditModal, setopenAddEditModal] = useState(false);
    let [selectedModule, setselectedModule] = useState<ModuleSetting>(new ModuleSetting());
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        lastUpdatedDate: QueryBuilderHelper.DefaultQueryFilterValue('lastUpdatedDate', null),
        sortBy: QueryBuilderHelper.DefaultSortValue(ModuleSettingField.NAME),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });

    const q_queryKey = [CachingEnum.MODULE_SETTING, queryStr]
    const { data, fetchStatus, deleteModuleSettingsById } = useModuleSettingsHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: ModuleSettingField.NAME,
            title: "Module Name",
            sortable: true,
            render: (record: ModuleSetting) => (AccessibilityHelper.hasAccess(AdminModules.MODULE_SETTINGS_EDIT) ?
                <Typography sx={{ cursor: "pointer" }} variant="caption" color="info" gutterBottom onClick={() => {
                    setopenAddEditModal(true);
                    setselectedModule(record);
                }} >{record?.[ModuleSettingField.NAME] ?? "--"}</Typography> : (record?.[ModuleSettingField.NAME] ?? "--")
            )
        },
        {
            accessor: ModuleSettingField.DESCRIPTION,
            sortable: true,
        },
        {
            accessor: ModuleSettingField.UPDATED_DATE,
            title: 'Last Updated',
            sortable: true,
            render: (record: ModuleSetting) => record?.[ModuleSettingField.UPDATED_DATE] ? DateHelper.getFormattedDate(record?.[ModuleSettingField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--",
        },
        {
            accessor: 'action',
            title: "Action",
            render: (record: ModuleSetting) => <>
                {AccessibilityHelper.hasAccess(AdminModules.MODULE_SETTINGS_EDIT) && <CustomIconButton toolTipName='Edit'
                    icon={<IconEdit />}
                    onClick={(e: any) => {
                        setopenAddEditModal(true);
                        setselectedModule(record);
                    }} />}
                {AccessibilityHelper.hasAccess(AdminModules.MODULE_SETTINGS_DELETE) && <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={(e: any) => { 
                        setopenDelete(true);
                        setselectedModule(record);
                    }} />}
            </>
        },
    ];

    useEffect(() => {
        setSearchParams(QueryBuilderHelper.CleanQueryDictionary(queryStr));
    }, [queryStr]);

    return <TableContainer $hadBoxShadow={true}>
        <CustomListHeader title={RouteHelper.getRouteDescription(RouteEnum.MODULE_SETTING)}
            subTitle=""
            headerButtons={<>
                {AccessibilityHelper.hasAccess(AdminModules.MODULE_SETTINGS_CREATE) && <CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Add New'
                    startIcon={<IconPlus />}
                    // isRoundedCorner={true}
                    onClick={(e: any) => {
                        setopenAddEditModal(true);
                        setselectedModule(new ModuleSetting());
                    }} />}
            </>}
        />
        <CustomListSearch searchValue={queryStr.searchString ?? ""}
            onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
            searchPlaceholder='Search Module'
            onClickLegendItem={(id) => setQueryStr(prev => ({ ...prev, status: id, pageNumber: 1 }))}>
            <FormDatePicker name="moduleUpdatedDate"
                hideLabel
                title=''
                placeholder='Last Updated Date'
                fieldValue={queryStr.lastUpdatedDate}
                handleOnChange={(date: Date) => setQueryStr(prev => ({
                    ...prev,
                    lastUpdatedDate: date ?? DateHelper.getFormattedDate(new Date(), "yyyy-MM-dd"),
                    pageNumber: 1,
                }))} />
            <CustomListSearchResetBtns onClickSearch={() => { }}
                onClickReset={() => {
                    setQueryStr(prev => ({
                        ...prev,
                        searchString: '',
                        lastUpdatedDate: null,
                        pageNumber: 1,
                     }))
                }}/>
        </CustomListSearch>
        <CustomListComponent
            columns={tableColumns}
            fetching={fetchStatus == "fetching"}
            idAccessor={(record: ModuleSetting) => `moduleSetting${record?.[ModuleSettingField.PUBLIC_ID]}`}
            records={data?.data?.list ?? []}
            totalRecords={data?.data?.metaData?.totalCount ?? 0}
            page={queryStr.pageNumber}
            recordsPerPage={queryStr.pageSize}
            defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
            onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
            setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
            onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={selectedModule?.[ModuleSettingField.NAME] ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
                setselectedModule(new ModuleSetting());
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(selectedModule?.[ModuleSettingField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteModuleSettingsById(selectedModule?.[ModuleSettingField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                        setselectedModule(new ModuleSetting());
                    }
                }
            }} />
        }

        {openAddEditModal && <AddEditModuleModal
            openAddEditModal={openAddEditModal}
            setopenAddEditModal={setopenAddEditModal}
            selectedModule={selectedModule}
            setselectedModule={setselectedModule} />}
    </TableContainer>
}
